import { useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { Link } from "react-router-dom";
import {
  Building2, HeartHandshake, ShieldCheck, Wrench, Clock, Home as HomeIcon, ArrowUpRight, Landmark,
} from "lucide-react";
import { Reveal, MaskedLines, Stagger, StaggerItem } from "../components/Reveal";
import { Overline, PrimaryLink, GhostLink, StatBlock } from "../components/Bits";
import { Marquee } from "../components/Marquee";
import { IMAGES, REGIONS, STATS } from "../lib/site";

const SERVICES = {
  "rent-to-rent": {
    icon: Building2,
    title: "Rent-to-Rent",
    tag: "For landlords",
    copy: "We lease your property on a long-term agreement, then manage everything end-to-end. One agreed monthly rent, no void-period hassle for you, and a simple, transparent 15% management fee.",
    points: ["3–5 year rent-to-rent lease", "No void-period hassle", "Full management & maintenance"],
    image: IMAGES.terracedDoors,
  },
  "supported-housing": {
    icon: HeartHandshake,
    title: "Supported Housing",
    tag: "For councils & providers",
    copy: "Safe, high-quality accommodation for vulnerable people — paired with 24/7 support pathways, compliance-first standards and close partnership with local authorities across the North West.",
    points: ["Vulnerable-person care pathways", "CQC & HMO compliant stock", "24/7 tenant support"],
    image: IMAGES.interiorDark,
  },
};

const MANIFESTO = [
  { n: "01", title: "People before portfolios", body: "Every home we take on becomes a stable, dignified place for someone to live. We house people first — the numbers follow." },
  { n: "02", title: "Certainty, not chaos", body: "Landlords get a long-term agreement and one agreed monthly rent — no chasing, no surprises, no end-of-month guesswork." },
  { n: "03", title: "Standards that hold up", body: "Every home is maintained to a compliance-first, Awaab's-Law-ready standard — inspected regularly and kept in genuinely good condition." },
  { n: "04", title: "Rooted in the North West", body: "From Manchester to Preston, we know these streets. Local teams, local knowledge, local accountability." },
];

const AUDIENCES = [
  { icon: Building2, t: "Landlords", d: "A long-term rent-to-rent agreement with one agreed monthly rent and full management — a genuinely hands-off let.", to: "/landlords", cta: "For landlords" },
  { icon: Landmark, t: "Local authorities", d: "Quality, compliant supported accommodation with rapid placement and 24/7 tenant support.", to: "/local-authorities", cta: "For councils" },
  { icon: HeartHandshake, t: "Support providers", d: "Safe, well-run homes and dependable partnership so you can deliver care with confidence.", to: "/services", cta: "How we help" },
];

const HeroToggle = ({ active, setActive }) => (
  <div className="inline-flex rounded-full glass p-1" data-testid="hero-service-toggle">
    {Object.entries(SERVICES).map(([key, s]) => (
      <button
        key={key}
        onClick={() => setActive(key)}
        data-testid={`toggle-${key}`}
        className={`relative rounded-full px-5 py-2.5 text-sm font-semibold transition-colors duration-300 ${
          active === key ? "text-ink" : "text-slate-300 hover:text-white"
        }`}
      >
        {active === key && (
          <motion.span
            layoutId="toggle-pill"
            className="absolute inset-0 rounded-full bg-amber"
            transition={{ type: "spring", stiffness: 400, damping: 32 }}
          />
        )}
        <span className="relative">{s.title}</span>
      </button>
    ))}
  </div>
);

export default function Home() {
  const [active, setActive] = useState("rent-to-rent");
  const S = SERVICES[active];
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const imgScale = useTransform(scrollYProgress, [0, 1], [1, 1.15]);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section ref={heroRef} className="relative min-h-[100svh] flex items-center overflow-hidden pt-32 pb-16">
        <div className="absolute inset-0 z-0 overflow-hidden">
          <motion.img
            src={IMAGES.skylineDay}
            alt="North West England skyline"
            style={{ y: imgY, scale: imgScale }}
            className="absolute inset-x-0 -top-[12%] h-[124%] w-full object-cover opacity-[0.6]"
          />
          <div className="absolute inset-0 bg-ink/40" />
          <div className="absolute inset-x-0 bottom-0 h-2/3 bg-gradient-to-b from-transparent to-ink" />
          <div className="absolute -top-32 -left-24 h-[40rem] w-[40rem] rounded-full bg-amber/10 blur-[130px] animate-pulse-glow" />
          <div className="absolute bottom-0 right-0 h-[30rem] w-[30rem] rounded-full bg-brandcyan/10 blur-[120px]" />
        </div>

        <div className="container-x relative z-10">
          <Reveal>
            <div className="inline-flex items-center gap-2 rounded-full glass px-4 py-1.5 text-xs font-mono uppercase tracking-[0.18em] text-slate-300">
              <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" />
              Trusted housing partner
            </div>
          </Reveal>

          <h1 className="mt-8 font-display font-extrabold tracking-tight leading-[0.9] text-5xl sm:text-7xl lg:text-[6.5rem] text-white">
            <MaskedLines lines={["Homes that", "hold people up."]} delay={0.15} />
          </h1>

          <Reveal delay={0.6}>
            <p className="mt-8 max-w-xl text-base sm:text-lg leading-relaxed text-slate-300">
              Insight Properties delivers long-term rent-to-rent for landlords and safe,
              supported housing for vulnerable people — right across the North West.
            </p>
          </Reveal>

          <Reveal delay={0.75}>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <PrimaryLink to="/services" testid="hero-cta-services">Explore our services</PrimaryLink>
              <GhostLink to="/landlords" testid="hero-cta-landlords">I'm a landlord</GhostLink>
            </div>
          </Reveal>
        </div>

        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.4 }}
          className="absolute bottom-8 left-1/2 -translate-x-1/2 text-slate-500 text-xs font-mono uppercase tracking-[0.3em]"
        >
          Scroll
        </motion.div>
      </section>

      {/* STATS */}
      <section className="container-x -mt-4 relative z-10">
        <Stagger className="grid grid-cols-2 lg:grid-cols-4 gap-4 lg:gap-6">
          {STATS.map((s, i) => (
            <StaggerItem key={i}>
              <StatBlock value={s.value} label={s.label} testid={`stat-${i}`} />
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* WHO WE WORK WITH */}
      <section className="container-x pt-24 lg:pt-32">
        <Reveal><Overline>Who we work with</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 max-w-2xl font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">
            A dependable housing partner for every side of the table.
          </h2>
        </Reveal>
        <Stagger className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          {AUDIENCES.map((a, i) => (
            <StaggerItem key={i}>
              <Link
                to={a.to}
                data-testid={`audience-${i}`}
                className="group card-surface h-full rounded-3xl p-8 flex flex-col hover:border-amber/30 transition-colors"
              >
                <a.icon className="h-8 w-8 text-amber" />
                <h3 className="mt-5 font-display font-bold text-xl text-white">{a.t}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed flex-1">{a.d}</p>
                <span className="mt-6 inline-flex items-center gap-1.5 text-sm text-amber">
                  {a.cta} <ArrowUpRight className="h-4 w-4 transition-transform group-hover:rotate-45" />
                </span>
              </Link>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* DUAL SERVICE SPOTLIGHT */}
      <section className="container-x py-24 lg:py-36">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
          <div className="max-w-2xl">
            <Reveal><Overline>What we do</Overline></Reveal>
            <Reveal delay={0.1}>
              <h2 className="mt-5 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">
                Two focused offers.<br />One human mission.
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}><HeroToggle active={active} setActive={setActive} /></Reveal>
        </div>

        <motion.div
          key={active}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-8 items-stretch"
        >
          <div className="relative overflow-hidden rounded-3xl min-h-[22rem] group">
            <img src={S.image} alt={S.title} className="absolute inset-0 h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/40 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-amber">{S.tag}</span>
              <div className="font-display font-bold text-3xl text-white mt-1">{S.title}</div>
            </div>
          </div>

          <div className="card-surface rounded-3xl p-8 lg:p-12 flex flex-col justify-center">
            <S.icon className="h-10 w-10 text-amber" />
            <p className="mt-6 text-lg leading-relaxed text-slate-200">{S.copy}</p>
            <ul className="mt-8 space-y-4">
              {S.points.map((p, i) => (
                <li key={i} className="flex items-center gap-3 text-slate-300">
                  <ShieldCheck className="h-5 w-5 text-brandcyan shrink-0" />
                  <span className="text-sm sm:text-base">{p}</span>
                </li>
              ))}
            </ul>
            <div className="mt-10">
              <PrimaryLink to="/services" testid="spotlight-cta">Learn more</PrimaryLink>
            </div>
          </div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="py-10 border-y border-white/10 bg-surface/30">
        <Marquee items={["Fully Managed", "Long-Term Lets", "Supported Living", "North West Proud", "People First"]} />
      </section>

      {/* MANIFESTO */}
      <section className="container-x py-24 lg:py-36">
        <Reveal><Overline>Our manifesto</Overline></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 max-w-3xl font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">
            Four beliefs that shape every home we manage.
          </h2>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-px bg-white/10 rounded-3xl overflow-hidden">
          {MANIFESTO.map((m, i) => (
            <Reveal key={m.n} delay={i * 0.08}>
              <div className="group h-full bg-ink hover:bg-surface transition-colors duration-500 p-8 lg:p-12" data-testid={`manifesto-${m.n}`}>
                <div className="flex items-baseline gap-4">
                  <span className="font-mono text-sm text-amber">{m.n}</span>
                  <span className="h-px flex-1 bg-white/10 group-hover:bg-amber/40 transition-colors duration-500" />
                </div>
                <h3 className="mt-6 font-display font-bold text-2xl lg:text-3xl text-white">{m.title}</h3>
                <p className="mt-4 text-slate-400 leading-relaxed">{m.body}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* NORTH WEST SPOTLIGHT */}
      <section className="relative py-24 lg:py-36 overflow-hidden">
        <div className="container-x">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <Reveal><Overline>Where we work</Overline></Reveal>
              <Reveal delay={0.1}>
                <h2 className="mt-5 font-display font-bold text-3xl sm:text-4xl lg:text-5xl tracking-tight text-white">
                  Built for the North West.
                </h2>
              </Reveal>
              <Reveal delay={0.2}>
                <p className="mt-6 max-w-lg text-slate-300 leading-relaxed">
                  Our teams live and work across the region — so we place people quickly, respond
                  fast, and hold ourselves accountable to the communities we serve.
                </p>
              </Reveal>
              <Stagger className="mt-10 flex flex-wrap gap-2.5" delay={0.1}>
                {REGIONS.map((r) => (
                  <StaggerItem key={r}>
                    <span className="inline-block rounded-full border border-white/12 px-4 py-2 text-sm text-slate-300 hover:border-amber/60 hover:text-amber transition-colors" data-testid={`region-${r.toLowerCase()}`}>
                      {r}
                    </span>
                  </StaggerItem>
                ))}
              </Stagger>
            </div>

            <Reveal delay={0.2}>
              <div className="relative rounded-3xl overflow-hidden aspect-[4/5] lg:aspect-square">
                <img src={IMAGES.salfordQuays} alt="Salford Quays at night" className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
                <div className="absolute top-6 left-6 glass rounded-2xl px-5 py-4">
                  <div className="font-display font-extrabold text-3xl text-gradient-amber">12+</div>
                  <div className="text-xs text-slate-300 mt-1">towns & cities covered</div>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* WHY US STRIP */}
      <section className="container-x pb-24 lg:pb-36">
        <Stagger className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[
            { icon: Wrench, t: "Full management", d: "Maintenance, compliance and tenancy handled entirely by us." },
            { icon: Clock, t: "One monthly rent", d: "A single agreed rent, set out in your long-term lease." },
            { icon: HomeIcon, t: "Homes people keep", d: "Stable, dignified accommodation with real support attached." },
          ].map((c, i) => (
            <StaggerItem key={i}>
              <div className="card-surface h-full rounded-3xl p-8 hover:border-amber/30 transition-colors duration-300" data-testid={`why-${i}`}>
                <c.icon className="h-8 w-8 text-amber" />
                <h3 className="mt-5 font-display font-bold text-xl text-white">{c.t}</h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">{c.d}</p>
              </div>
            </StaggerItem>
          ))}
        </Stagger>
      </section>

      {/* CTA */}
      <section className="container-x pb-28 lg:pb-40">
        <Reveal>
          <div className="relative overflow-hidden rounded-[2rem] card-surface px-8 py-16 lg:px-20 lg:py-24 text-center">
            <div className="absolute -top-24 left-1/2 -translate-x-1/2 h-72 w-72 rounded-full bg-amber/15 blur-[100px]" />
            <div className="relative">
              <h2 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white max-w-3xl mx-auto leading-[0.95]">
                Let's find the right home for your property — or your people.
              </h2>
              <p className="mt-6 text-slate-300 max-w-xl mx-auto">
                Whether you're a landlord seeking a hands-off let or a council seeking quality
                supported housing, we'd love to talk.
              </p>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <PrimaryLink to="/contact" testid="cta-contact">Get in touch</PrimaryLink>
                <GhostLink to="/about" testid="cta-about">About Insight <ArrowUpRight className="hidden" /></GhostLink>
              </div>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
